import React, { useState, useCallback } from 'react';
import { styled as styled2 } from '@compiled/react';
import { useFragment, graphql } from 'react-relay';
import Heading from '@atlaskit/heading';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import BranchCreationOptionsWithIssueKeyHighlight from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/software/components/branch-creation-options-with-issue-key-highlight';
import CollaborateOnCode from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/software/components/collaborate-on-code';
import ConnectWorkflow from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/software/components/connect-workflow';
import UiDevPanelMagnifiedWithBitbucketLogo from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/software/components/ui-dev-panel-magnified-with-bitbucket-logo';
import { useIntl } from '@atlassian/jira-intl';
import type { main_softwareCodePage_ReadyToConnect$key } from '@atlassian/jira-relay/src/__generated__/main_softwareCodePage_ReadyToConnect.graphql';
import collaborateImg from './assets/collaborate.svg';
import connectWorkflowImg from './assets/connectWorkflow.svg';
import getVisibilityImg from './assets/getVisibility.svg';
import issueKeysImg from './assets/issueKeys.png';
import BenefitToConnect from './benefit-to-connect';
import ConnectorSection from './connector-section';
import EmbeddedMarketplace from './embedded-marketplace';
import messages from './messages';

type ReadyToConnectProps = {
	readyToConnectData: main_softwareCodePage_ReadyToConnect$key;
	isAdmin: boolean;
	onRefetch: () => void;
	onShowOneClickConnect: () => void;
};

export const ReadyToConnect = ({
	readyToConnectData,
	isAdmin,
	onRefetch,
	onShowOneClickConnect,
}: ReadyToConnectProps) => {
	const { formatMessage } = useIntl();
	// Storing the ref value in state to trigger a re-render when it changes
	const [marketplaceSectionElement, setMarketplaceSectionElement] =
		useState<HTMLButtonElement | null>(null);
	const onRefChange = useCallback((node: HTMLButtonElement | null) => {
		setMarketplaceSectionElement(node);
	}, []);
	const data = useFragment(
		graphql`
			fragment main_softwareCodePage_ReadyToConnect on Query {
				...main_softwareCodePage_ConnectorSection
			}
		`,
		readyToConnectData,
	);

	return (
		<Wrapper>
			<MainHeaderContainer>
				<BenefitToConnect
					renderImage={
						ff('isotopes-consolidate-jfe-assets_pddkf') ? (
							<UiDevPanelMagnifiedWithBitbucketLogo alt="" />
						) : (
							<ValueImage src={getVisibilityImg} alt="" />
						)
					}
					title={messages.topSectionValueTitle}
					description={messages.topSectionValueDescriptionMessageSpork}
					header
				/>
			</MainHeaderContainer>
			<ConnectorSection
				connectorSectionData={data}
				isAdmin={isAdmin}
				onShowOneClickConnect={onShowOneClickConnect}
				onRefetch={onRefetch}
				marketplaceSectionElement={marketplaceSectionElement}
			/>
			<HorizontalLine />

			<HeaderContainer>
				<Heading size="medium">
					<HeaderTitleContainer>{formatMessage(messages.sectionTitle)}</HeaderTitleContainer>
				</Heading>
				<HeaderText>{formatMessage(messages.sectionDescriptionSpork)}</HeaderText>
			</HeaderContainer>
			<HorizontalLine />

			<BenefitToConnect
				renderImage={
					ff('isotopes-consolidate-jfe-assets_pddkf') ? (
						<CollaborateOnCode alt="" />
					) : (
						<ValueImage src={collaborateImg} />
					)
				}
				title={messages.value1Title}
				description={messages.value1Description}
				imagePosition="right"
			/>

			<SeparatorLine />
			<SeparatorLine />

			<BenefitToConnect
				renderImage={
					ff('isotopes-consolidate-jfe-assets_pddkf') ? (
						<BranchCreationOptionsWithIssueKeyHighlight alt="" />
					) : (
						<ValueImage src={issueKeysImg} />
					)
				}
				title={messages.value2TitleSpork}
				description={messages.value2Description}
				header={false}
			/>

			<SeparatorLine />
			<SeparatorLine />

			<BenefitToConnect
				renderImage={
					ff('isotopes-consolidate-jfe-assets_pddkf') ? (
						<ConnectWorkflow alt="" />
					) : (
						<ValueImage src={connectWorkflowImg} />
					)
				}
				title={messages.value3Title}
				description={messages.value3Description}
				imagePosition="right"
				header={false}
			/>

			<HorizontalLine />
			<EmbeddedMarketplace onRefChange={onRefChange} onRefetch={onRefetch} />
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled2.div({
	height: '100%',
	width: '688px',
	display: 'flex',
	flexDirection: 'column',
	padding: token('space.100', '8px'),
	margin: `0 ${token('space.500', '40px')}`,
	borderRadius: token('space.100', '8px'),
	marginLeft: 'auto',
	marginRight: 'auto',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ValueImage = styled2.img({
	maxWidth: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HorizontalLine = styled2.hr({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.border', colors.N40),
	border: '1px solid',
	margin: `${token('space.500', '40px')} 0`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SeparatorLine = styled2.div({
	marginTop: token('space.500', '40px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MainHeaderContainer = styled2.div({
	display: 'block',
	margin: 0,
	width: '100%',
	fontSize: token('space.200', '16px'),
	lineHeight: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderContainer = styled2.div({
	display: 'block',
	textAlign: 'center',
	margin: 0,
	width: '100%',
	fontSize: token('space.200', '16px'),
	lineHeight: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderText = styled2.div({
	whiteSpace: 'break-spaces',
	textAlign: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderTitleContainer = styled2.div({
	marginBottom: token('space.200', '16px'),
	marginTop: 0,
});
